import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, Users, ArrowRight, Phone, Mail, MessageCircle } from 'lucide-react';

// 홈페이지 메인 컴포넌트 - 고객 유형별 선택 페이지
const Home: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* 메인 히어로 섹션 */}
      <section className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto text-center">
          {/* 메인 타이틀 */}
          <div className="mb-16">
            <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
              ChatGPT 교육 전문가
            </h1>
            <p className="text-xl lg:text-2xl text-gray-600 mb-4">
              비즈니스커리어컨설팅
            </p>
            <p className="text-lg text-gray-500">
              AI 시대에 맞는 혁신적인 교육 솔루션
            </p>
          </div>

          {/* 고객 유형 선택 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* 기관고객 (B2B) */}
            <Link
              to="/b2b"
              className="group bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 p-8 border-2 border-transparent hover:border-primary-200"
            >
              <div className="text-center">
                <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-primary-200 transition-colors">
                  <Building2 className="w-10 h-10 text-primary-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  기관고객
                </h2>
                <p className="text-lg text-gray-600 mb-6">
                  공공기관, 지자체, 기업을 위한<br />
                  <strong>강의섭외 서비스</strong>
                </p>
                <div className="space-y-3 text-left mb-6">
                  <div className="flex items-center text-gray-700">
                    <div className="w-2 h-2 bg-primary-500 rounded-full mr-3"></div>
                    <span>공공기관 디지털 전환 교육</span>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <div className="w-2 h-2 bg-primary-500 rounded-full mr-3"></div>
                    <span>지자체 업무 효율성 향상</span>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <div className="w-2 h-2 bg-primary-500 rounded-full mr-3"></div>
                    <span>기업 맞춤형 AI 교육</span>
                  </div>
                </div>
                <div className="flex items-center justify-center text-primary-600 font-semibold group-hover:text-primary-700">
                  <span>강의섭외 문의</span>
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>

            {/* 개인고객 (B2C) */}
            <Link
              to="/b2c"
              className="group bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 p-8 border-2 border-transparent hover:border-secondary-200"
            >
              <div className="text-center">
                <div className="w-20 h-20 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-secondary-200 transition-colors">
                  <Users className="w-10 h-10 text-secondary-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  개인고객
                </h2>
                <p className="text-lg text-gray-600 mb-6">
                  개인 역량 개발을 위한<br />
                  <strong>챌린지 프로그램</strong>
                </p>
                <div className="space-y-3 text-left mb-6">
                  <div className="flex items-center text-gray-700">
                    <div className="w-2 h-2 bg-secondary-500 rounded-full mr-3"></div>
                    <span>ChatGPT 자기소개서 작성</span>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <div className="w-2 h-2 bg-secondary-500 rounded-full mr-3"></div>
                    <span>면접 준비 챌린지</span>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <div className="w-2 h-2 bg-secondary-500 rounded-full mr-3"></div>
                    <span>창업 아이디어 발굴</span>
                  </div>
                </div>
                <div className="flex items-center justify-center text-secondary-600 font-semibold group-hover:text-secondary-700">
                  <span>챌린지 참여</span>
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          </div>

          {/* 연락처 정보 */}
          <div className="mt-16 bg-white rounded-xl shadow-lg p-6 max-w-2xl mx-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">문의 및 상담</h3>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:010-9321-0093"
                className="flex items-center justify-center space-x-2 bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Phone className="w-5 h-5" />
                <span>010-9321-0093</span>
              </a>
              <a
                href="mailto:ohdana0808@daum.net"
                className="flex items-center justify-center space-x-2 border-2 border-primary-600 text-primary-600 px-6 py-3 rounded-lg hover:bg-primary-50 transition-colors"
              >
                <Mail className="w-5 h-5" />
                <span>이메일 문의</span>
              </a>
              <button className="flex items-center justify-center space-x-2 border-2 border-secondary-600 text-secondary-600 px-6 py-3 rounded-lg hover:bg-secondary-50 transition-colors">
                <MessageCircle className="w-5 h-5" />
                <span>카카오톡 채널</span>
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;

